import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  FileText, 
  Zap, 
  Droplets, 
  Shield, 
  GraduationCap,
  Heart,
  Construction,
  Route,
  Trash2,
  Plus,
  Eye,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const issueIcons = {
  healthcare: Heart,
  education_quality: GraduationCap,
  federal_highways: Route,
  local_roads: Construction,
  power: Zap,
  water: Droplets,
  waste: Trash2,
  security_police: Shield
};

const levelColors = {
  federal: 'bg-red-100 text-red-800 border-red-200',
  state: 'bg-blue-100 text-blue-800 border-blue-200',
  local: 'bg-green-100 text-green-800 border-green-200'
};

export default function SurveyTemplates({ user }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('all');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/survey-templates`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const createFromTemplate = async (templateId) => {
    setCreating(templateId);
    try {
      const response = await fetch(`${API}/surveys/from-template/${templateId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const survey = await response.json();
        toast.success('Survey created from template!');
        navigate(`/admin/surveys/${survey.survey_id}/edit`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create survey');
      }
    } catch (error) {
      console.error('Error creating survey:', error);
      toast.error('Failed to create survey');
    } finally {
      setCreating(null);
    }
  };

  const filteredTemplates = selectedLevel === 'all' 
    ? templates 
    : templates.filter(t => t.governance_level === selectedLevel);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="survey-templates">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Dashboard
                </Button>
              </Link>
              <div className="h-6 w-px bg-border"></div>
              <h1 className="font-semibold">Survey Templates</h1>
            </div>
            <Link to="/admin/surveys/new">
              <Button variant="outline" size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Custom Survey
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-2">Ready-to-Use Survey Templates</h2>
          <p className="text-muted-foreground">
            Pre-designed surveys based on governance responsibilities. Click "Use Template" to create a survey instantly.
          </p>
        </div>

        {/* Level Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button 
            variant={selectedLevel === 'all' ? 'default' : 'outline'}
            onClick={() => setSelectedLevel('all')}
            size="sm"
          >
            All Templates ({templates.length})
          </Button>
          <Button 
            variant={selectedLevel === 'federal' ? 'default' : 'outline'}
            onClick={() => setSelectedLevel('federal')}
            size="sm"
            className={selectedLevel === 'federal' ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            Federal ({templates.filter(t => t.governance_level === 'federal').length})
          </Button>
          <Button 
            variant={selectedLevel === 'state' ? 'default' : 'outline'}
            onClick={() => setSelectedLevel('state')}
            size="sm"
            className={selectedLevel === 'state' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            State ({templates.filter(t => t.governance_level === 'state').length})
          </Button>
          <Button 
            variant={selectedLevel === 'local' ? 'default' : 'outline'}
            onClick={() => setSelectedLevel('local')}
            size="sm"
            className={selectedLevel === 'local' ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            Local ({templates.filter(t => t.governance_level === 'local').length})
          </Button>
        </div>

        {/* Templates Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {filteredTemplates.map((template) => {
            const IconComponent = issueIcons[template.linked_issue] || FileText;
            return (
              <Card key={template.template_id} className="card-hover" data-testid={`template-${template.template_id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <IconComponent className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{template.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={levelColors[template.governance_level]}>
                            {template.governance_level}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {template.policy_category}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="mb-4">{template.description}</CardDescription>
                  
                  <div className="text-sm text-muted-foreground mb-4">
                    <span className="font-medium">{template.questions.length} questions</span>
                    <span className="mx-2">•</span>
                    <span>
                      {template.questions.filter(q => q.question_type === 'rating').length} rating, 
                      {' '}{template.questions.filter(q => q.question_type === 'multiple_choice').length} choice,
                      {' '}{template.questions.filter(q => q.question_type === 'open_ended').length} open
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button 
                      onClick={() => createFromTemplate(template.template_id)}
                      disabled={creating === template.template_id}
                      className="flex-1"
                    >
                      {creating === template.template_id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Plus className="mr-2 h-4 w-4" />
                          Use Template
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredTemplates.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Templates Found</h3>
              <p className="text-muted-foreground">
                No templates available for the selected filter.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
